import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Inbox, Mail, Phone, Calculator, History, Building2 } from "lucide-react";

const STATUTS = [
  { value: "nouvelle", label: "Nouvelle", cls: "bg-amber-100 text-amber-800" },
  { value: "contactee", label: "Contactée", cls: "bg-sky-100 text-sky-800" },
  { value: "en_etude", label: "En étude", cls: "bg-violet-100 text-violet-800" },
  { value: "adherente", label: "Adhérente", cls: "bg-emerald-100 text-emerald-800" },
  { value: "perdue", label: "Perdue", cls: "bg-slate-200 text-slate-700" },
];
const statutOf = (v) => STATUTS.find((s) => s.value === v) || STATUTS[0];
const eur = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("fr-FR", { style: "currency", currency: "EUR" }));
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

function StatusTabs({ value, counts, onChange }) {
  const total = Object.values(counts || {}).reduce((a, b) => a + b, 0);
  const tabs = [{ value: "", label: "Toutes", count: total }, ...STATUTS.map((s) => ({ ...s, count: counts?.[s.value] || 0 }))];
  return (
    <div className="flex flex-wrap gap-2" data-testid="prospects-tabs">
      {tabs.map((t) => (
        <button key={t.value || "all"} type="button" onClick={() => onChange(t.value)}
          data-testid={`prospects-tab-${t.value || "all"}`}
          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${value === t.value ? "bg-[#1B2A4A] text-white border-[#1B2A4A]" : "bg-white text-slate-700 hover:border-[#C5A059]"}`}>
          {t.label} <span className="ml-1 opacity-70" data-testid={`prospects-count-${t.value || "all"}`}>{t.count}</span>
        </button>
      ))}
    </div>
  );
}

function SimulationSummary({ sim }) {
  if (!sim) return <div className="text-xs text-slate-400">Sans simulation jointe</div>;
  return (
    <div className="text-xs text-slate-600 space-y-0.5" data-testid="prospect-simulation">
      <div className="flex items-center gap-1 font-medium text-slate-800"><Calculator className="h-3 w-3" />{sim.sectorLabel} · {sim.count} salarié(s) · {Math.round(sim.hours)} h</div>
      <div>Mission HT {eur(sim.mission)} · sous-total HT {eur(sim.subtotalHT)}</div>
      {sim.totalTTC !== null && sim.totalTTC !== undefined && <div>TTC simulé {eur(sim.totalTTC)}</div>}
    </div>
  );
}

function ProspectRow({ p, onEdit, onConvert }) {
  const s = statutOf(p.statut);
  return (
    <tr className="border-b last:border-b-0 align-top hover:bg-slate-50" data-testid={`prospect-row-${p.id}`}>
      <td className="py-3 pr-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(p.created_at)}</td>
      <td className="py-3 pr-3">
        <div className="font-medium text-slate-900">{p.organisation}</div>
        <div className="text-xs text-slate-600 flex items-center gap-1"><Mail className="h-3 w-3" /><a href={`mailto:${p.email}`} className="hover:underline">{p.email}</a></div>
        {p.telephone && <div className="text-xs text-slate-600 flex items-center gap-1"><Phone className="h-3 w-3" />{p.telephone}</div>}
      </td>
      <td className="py-3 pr-3 max-w-xs text-sm text-slate-700 whitespace-pre-line">{p.besoin}</td>
      <td className="py-3 pr-3"><SimulationSummary sim={p.simulation} /></td>
      <td className="py-3 pr-3"><Badge className={s.cls} data-testid={`prospect-statut-${p.id}`}>{s.label}</Badge></td>
      <td className="py-3 text-right">
        <div className="flex flex-col items-end gap-1">
          <Button size="sm" variant="outline" onClick={() => onEdit(p)} data-testid={`prospect-edit-${p.id}`}>Suivre</Button>
          {p.statut === "adherente" && !p.entreprise_id && (
            <Button size="sm" onClick={() => onConvert(p)} data-testid={`prospect-convert-${p.id}`} className="bg-[#C5A059] hover:bg-[#A88846] text-white">
              <Building2 className="h-3 w-3 mr-1" />Créer l'entreprise adhérente
            </Button>
          )}
          {p.entreprise_id && (
            <Link to="/app/entreprises" className="text-xs text-emerald-700 underline" data-testid={`prospect-entreprise-link-${p.id}`}>Entreprise créée</Link>
          )}
        </div>
      </td>
    </tr>
  );
}

function StatusDialog({ prospect, onClose, onSaved }) {
  const [statut, setStatut] = useState(prospect?.statut || "nouvelle");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setStatut(prospect?.statut || "nouvelle"); setNote(""); }, [prospect]);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/prospects/${prospect.id}/statut`, { statut, note: note.trim() || null });
      toast.success("Suivi mis à jour");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mise à jour impossible");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!prospect} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="prospect-status-dialog">
        <DialogHeader><DialogTitle>Suivi — {prospect?.organisation}</DialogTitle><DialogDescription>Changez le statut de la demande ; chaque changement est historisé.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Nouveau statut</Label>
            <Select value={statut} onValueChange={setStatut}>
              <SelectTrigger data-testid="prospect-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Note {statut === "perdue" ? "(motif obligatoire)" : "(facultative)"}</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} data-testid="prospect-status-note" placeholder="Ex. appel du 12/10, rendez-vous fixé…" />
          </div>
          <div className="border-t pt-3">
            <div className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-2"><History className="h-3 w-3" />Historique</div>
            <ul className="space-y-1 max-h-40 overflow-y-auto" data-testid="prospect-history">
              {(prospect?.historique || []).slice().reverse().map((h, i) => (
                <li key={i} className="text-xs text-slate-600">
                  <span className="font-medium">{statutOf(h.statut).label}</span> · {fmtDate(h.at)} · {h.by}{h.note ? ` — ${h.note}` : ""}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="prospect-status-cancel">Annuler</Button>
          <Button onClick={save} disabled={saving || (statut === "perdue" && !note.trim())} data-testid="prospect-status-save" className="bg-[#1B2A4A] text-white hover:bg-[#243458]">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Prospects() {
  const [filter, setFilter] = useState("");
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/prospects", { params: filter ? { statut: filter } : {} });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chargement impossible");
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const convert = async (p) => {
    try {
      await api.post(`/prospects/${p.id}/creer-entreprise`);
      toast.success(`Entreprise « ${p.organisation} » créée — à compléter (SIRET, statut) dans Entreprises`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Création impossible"); }
  };

  return (
    <div className="space-y-6" data-testid="prospects-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Demandes simulateur</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">Demandes envoyées depuis la page publique « Présentation & simulateur », avec le consentement des visiteurs. Suivez chaque prospect jusqu'à son adhésion.</p>
      </div>
      <StatusTabs value={filter} counts={data.counts} onChange={setFilter} />
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Inbox className="h-4 w-4" />Demandes ({data.items.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? <div className="text-sm text-slate-500">Chargement…</div> : data.items.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center" data-testid="prospects-empty">Aucune demande pour ce filtre.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="prospects-table">
                <thead className="text-xs uppercase text-slate-500 border-b">
                  <tr><th className="text-left py-2">Reçue</th><th className="text-left py-2">Organisation</th><th className="text-left py-2">Besoin</th><th className="text-left py-2">Simulation</th><th className="text-left py-2">Statut</th><th className="text-right py-2">Actions</th></tr>
                </thead>
                <tbody>{data.items.map((p) => <ProspectRow key={p.id} p={p} onEdit={setEditing} onConvert={convert} />)}</tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      <StatusDialog prospect={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
